import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/contexts/ToastContext';
import { useProfiler } from '@/hooks/useProfiler';
import type { Order, Table, OrderWithDetails } from '@/types/common';
import { formatCurrency, formatDate } from '@/utils/format';
import { motion, AnimatePresence } from 'framer-motion';
import PaidOrderDetailsModal from '@/components/Order/PaidOrderDetailsModal';
import { Profiler } from 'react';

interface OrderWithTable extends Order {
  table: Table;
}

export default function OrdersPage() {
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState<OrderWithTable[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<Order['status'] | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const ordersPerPage = 10;
  const { addToast } = useToast();
  const lastOrderRef = useRef<HTMLLIElement>(null);
  const navigate = useNavigate();
  const lastLoadedIndexRef = useRef(0);
  const [selectedPaidOrder, setSelectedPaidOrder] = useState<OrderWithDetails | null>(null);
  const onRender = useProfiler('OrdersPage');

  // Handle URL parameter on mount
  useEffect(() => {
    const status = searchParams.get('status');
    if (status === 'active') {
      // Set filter to show non-paid orders
      setStatusFilter(prev => prev === 'paid' ? 'all' : prev);
    }
  }, [searchParams]);

  // Fetch orders with pagination
  const fetchOrders = useCallback(async (pageNumber: number) => {
    try {
      setLoading(true);
      const from = (pageNumber - 1) * ordersPerPage;
      const to = from + ordersPerPage - 1;

      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select(`
          *,
          table:tables(*)
        `)
        .order('updated_at', { ascending: false })
        .range(from, to);

      if (ordersError) throw ordersError;

      if (pageNumber === 1) {
        setOrders(ordersData as OrderWithTable[]);
      } else {
        const previousHeight = document.documentElement.scrollHeight;
        
        setOrders(prev => {
          const newOrders = [...prev, ...(ordersData as OrderWithTable[])];
          lastLoadedIndexRef.current = prev.length;
          
          // Schedule scroll after state update and render
          requestAnimationFrame(() => {
            const newHeight = document.documentElement.scrollHeight;
            const scrollOffset = newHeight - previousHeight;
            
            if (scrollOffset > 0) {
              window.scrollBy({
                top: scrollOffset - 100, // Subtract some padding
                behavior: 'smooth'
              });
            }
          });
          
          return newOrders;
        });
      }

      setHasMore(ordersData.length === ordersPerPage);
    } catch (error) {
      console.error('Error fetching orders:', error);
      addToast({
        type: 'error',
        message: 'Failed to load orders',
      });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  // Initial fetch
  useEffect(() => {
    void fetchOrders(1);
  }, [fetchOrders]);

  // Modify the useEffect for real-time updates
  useEffect(() => {
    const channel = supabase.channel('orders-changes');

    channel
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'orders' },
        async (payload) => {
          // Handle different event types
          if (payload.eventType === 'INSERT') {
            const { data: newOrder, error } = await supabase
              .from('orders')
              .select('*, table:tables(*)')
              .eq('id', payload.new.id)
              .single();

            if (!error && newOrder) {
              setOrders(prev => [newOrder, ...prev]);
            }
          } else if (payload.eventType === 'UPDATE') {
            setOrders(prev => {
              const updatedOrders = prev.map(order => 
                order.id === payload.new.id 
                  ? { ...order, ...payload.new }
                  : order
              );
              // Sort by updated_at in descending order
              return updatedOrders.sort((a, b) => 
                new Date(b.updated_at || b.created_at).getTime() - 
                new Date(a.updated_at || a.created_at).getTime()
              );
            });
          } else if (payload.eventType === 'DELETE') {
            setOrders(prev => prev.filter(order => order.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      void channel.unsubscribe();
    };
  }, []);

  // Remove the fetchOrders call from updateOrderStatus
  const updateOrderStatus = async (orderId: string, newStatus: Order['status']) => {
    try {
      // Get current user's role and order status
      const [{ data: userData, error: userError }, { data: currentOrder, error: fetchError }] = await Promise.all([
        supabase.from('users')
          .select('role')
          .eq('id', (await supabase.auth.getUser()).data.user?.id)
          .single(),
        supabase.from('orders')
          .select('status')
          .match({ id: orderId })
          .single()
      ]);

      if (userError) throw userError;
      if (fetchError) throw fetchError;
      if (!currentOrder) throw new Error('Order not found');

      // Validate status transition
      const validTransitions = {
        'pending': ['preparing'],
        'preparing': ['ready'],
        'ready': ['delivered'],
        'delivered': ['paid']
      };

      if (!validTransitions[currentOrder.status]?.includes(newStatus)) {
        throw new Error(`Invalid status transition from ${currentOrder.status} to ${newStatus}`);
      }

      // Validate user role for kitchen-only statuses
      if ((newStatus === 'preparing' || newStatus === 'ready') && 
          !['kitchen', 'admin'].includes(userData.role)) {
        throw new Error('Only kitchen staff can change order status to preparing or ready');
      }

      // Update the status
      const { error: updateError } = await supabase
        .from('orders')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .match({ id: orderId });

      if (updateError) throw updateError;

      // Local state will be updated by the subscription
      addToast({
        type: 'success',
        message: `Order status updated to ${newStatus}`,
      });
    } catch (error) {
      console.error('Error updating order status:', error);
      addToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to update order status',
      });
    }
  };

  // Filter orders
  const filteredOrders = orders.filter(order => {
    const matchesStatus = statusFilter === 'all' 
      ? (searchParams.get('status') === 'active' ? order.status !== 'paid' : true)
      : order.status === statusFilter;
    const matchesSearch = searchQuery === '' || 
      order.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.table.number.toString().includes(searchQuery);
    return matchesStatus && matchesSearch;
  });

  const handleRowClick = async (order: OrderWithTable) => {
    if (order.status === 'paid') {
      try {
        const { data: completeOrder, error } = await supabase
          .from('orders')
          .select(`
            *,
            table:tables(*),
            items:order_items(
              id,
              order_id,
              menu_item_id,
              quantity,
              notes,
              created_at,
              menu_item:menu_items(
                id,
                name,
                description,
                price,
                category_id,
                subcategory_id,
                active,
                created_at,
                category:categories(*)
              )
            ),
            payments:payments(
              tip_amount,
              custom_amount
            )
          `)
          .eq('id', order.id)
          .single();

        if (error) throw error;

        // Calculate total tip amount and custom amount from all payments
        const totalTipAmount = completeOrder.payments?.reduce((sum, payment) => sum + (payment.tip_amount || 0), 0) || 0;
        const totalCustomAmount = completeOrder.payments?.reduce((sum, payment) => sum + (payment.custom_amount || 0), 0) || 0;

        // Restructure the data to match our OrderWithDetails type
        const orderWithPayment = {
          ...completeOrder,
          tip_amount: totalTipAmount,
          custom_amount: totalCustomAmount
        };

        setSelectedPaidOrder(orderWithPayment as OrderWithDetails);
      } catch (error) {
        console.error('Error fetching order details:', error);
        addToast({
          type: 'error',
          message: 'Failed to load order details',
        });
      }
    } else {
      navigate(`/orders/${order.id}`);
    }
  };

  // Get status badge classes
  const getStatusBadgeClasses = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200';
      case 'preparing':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200';
      case 'ready':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200';
      case 'delivered':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200';
      case 'paid':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-200';
    }
  };

  const handleLoadMore = useCallback(() => {
    const nextPage = page + 1;
    setPage(nextPage);
    void fetchOrders(nextPage);
  }, [page, fetchOrders]);

  const handleDeleteOrder = async (orderId: string, tableId: string) => {
    try {
      // Delete the order
      const { error: deleteError } = await supabase
        .from('orders')
        .delete()
        .eq('id', orderId);

      if (deleteError) throw deleteError;

      // Update table status to available
      const { error: tableError } = await supabase
        .from('tables')
        .update({ status: 'available' })
        .eq('id', tableId);

      if (tableError) throw tableError;

      addToast({
        type: 'success',
        message: 'Order deleted successfully'
      });

      // Update local state
      setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
    } catch (error) {
      console.error('Error deleting order:', error);
      addToast({
        type: 'error',
        message: 'Failed to delete order'
      });
    }
  };

  const handleCancelOrder = async (orderId: string, tableId: string) => {
    try {
      // Update order status to cancelled
      const { error: updateError } = await supabase
        .from('orders')
        .update({ 
          status: 'cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('id', orderId);

      if (updateError) throw updateError;

      // Update table status to available
      const { error: tableError } = await supabase
        .from('tables')
        .update({ status: 'available' })
        .eq('id', tableId);

      if (tableError) throw tableError;

      addToast({
        type: 'success',
        message: 'Order cancelled successfully'
      });

      // Update local state
      setOrders(prevOrders => prevOrders.map(order => 
        order.id === orderId 
          ? { ...order, status: 'cancelled' }
          : order
      ));
    } catch (error) {
      console.error('Error cancelling order:', error);
      addToast({
        type: 'error',
        message: 'Failed to cancel order'
      });
    }
  };

  const handleDeletePaidOrder = async (orderId: string) => {
    try {
      // Get the table ID from the order
      const order = orders.find(o => o.id === orderId);
      if (!order) {
        throw new Error('Order not found');
      }

      // Delete order items first
      const { error: itemsError } = await supabase
        .from('order_items')
        .delete()
        .eq('order_id', orderId);

      if (itemsError) throw itemsError;

      // Delete payments
      const { error: paymentsError } = await supabase
        .from('payments')
        .delete()
        .eq('order_id', orderId);

      if (paymentsError) throw paymentsError;

      // Delete the order
      const { error: deleteError } = await supabase
        .from('orders')
        .delete()
        .eq('id', orderId);

      if (deleteError) throw deleteError;

      // Update table status to available
      const { error: tableError } = await supabase
        .from('tables')
        .update({ status: 'available' })
        .eq('id', order.table.id);

      if (tableError) throw tableError;

      addToast({
        type: 'success',
        message: 'Order deleted successfully'
      });

      // Update local state
      setOrders(prevOrders => prevOrders.filter(o => o.id !== orderId));
      setSelectedPaidOrder(null); // Close the modal
    } catch (error) {
      console.error('Error deleting order:', error);
      addToast({
        type: 'error',
        message: 'Failed to delete order'
      });
    }
  };

  // Add these action buttons to the order row
  const renderActionButtons = (order: OrderWithTable) => {
    const isPaid = order.status === 'paid';
    const isActive = ['pending', 'preparing', 'ready', 'delivered'].includes(order.status);

    if (!isActive && !isPaid) return null;

    return (
      <div className="flex gap-2">
        {isActive && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCancelOrder(order.id, order.table.id);
            }}
            className="btn btn-icon btn-danger"
            title="Cancel Order"
          >
            <i className="fas fa-times" />
          </button>
        )}
        {(isPaid || isActive) && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
                handleDeleteOrder(order.id, order.table.id);
              }
            }}
            className="btn btn-icon btn-danger"
            title="Delete Order"
          >
            <i className="fas fa-trash" />
          </button>
        )}
      </div>
    );
  };

  // Update the table row to include the action buttons
  const renderOrderRow = (order: OrderWithTable) => (
    <tr
      key={order.id}
      onClick={() => handleRowClick(order)}
      className="hover:bg-gray-50 dark:hover:bg-dark-bg-lighter cursor-pointer"
    >
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-dark-text-primary">
        Table #{order.table.number}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-dark-text-primary">
        {formatCurrency(order.total)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={getStatusBadgeClasses(order.status)}>
          {order.status}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-dark-text-secondary">
        {formatDate(order.updated_at || order.created_at)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        {renderActionButtons(order)}
      </td>
    </tr>
  );

  if (loading) {
    return (
      <Profiler id="OrdersPage-Loading" onRender={onRender}>
        <div className="flex items-center justify-center min-h-screen-75">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-600"></div>
        </div>
      </Profiler>
    );
  }

  return (
    <Profiler id="OrdersPage-Content" onRender={onRender}>
      <div className="container mx-auto px-4 pt-4">
        {/* Header */}
        <div className="sm:flex sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-dark-text-primary">
              Orders
            </h1>
            <p className="mt-2 text-sm text-gray-700 dark:text-dark-text-secondary">
              Manage and track all orders
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <Link
              to="/orders/new"
              className="inline-flex items-center justify-center px-6 py-3 text-base font-semibold text-white rounded-lg transition-all duration-300 ease-out bg-gradient-to-r from-cyan-500 to-blue-500 shadow-[0_4px_0_0_#0369a1] hover:shadow-[0_6px_0_0_#0369a1] hover:-translate-y-[2px] active:translate-y-[2px] active:shadow-[0_2px_0_0_#0369a1] touch-manipulation"
            >
              <i className="fas fa-plus mr-3" aria-hidden="true" />
              <span>New Order</span>
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 sm:flex sm:items-center sm:justify-between px-1">
          {/* Status Filter */}
          <div className="flex flex-wrap gap-2 mb-4 sm:mb-0">
            {(['all', 'pending', 'preparing', 'ready', 'delivered', 'paid'] as const).map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`${
                  statusFilter === status
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'text-gray-500 dark:text-dark-text-secondary hover:text-gray-700 dark:hover:text-dark-text-primary'
                } px-3 py-2 text-sm font-medium rounded-md`}
              >
                {status === 'all' ? 'All Orders' : status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="max-w-lg w-full">
            <label htmlFor="search" className="sr-only">
              Search orders
            </label>
            <div className="relative px-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className="fas fa-search text-gray-400" />
              </div>
              <input
                type="text"
                id="search"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-dark-border rounded-md leading-5 bg-white dark:bg-dark-bg-light text-gray-900 dark:text-dark-text-primary placeholder-gray-500 dark:placeholder-dark-text-secondary focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Search by order ID or table number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Orders List */}
        <div className="bg-white dark:bg-dark-bg-light shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200 dark:divide-dark-border">
            <AnimatePresence mode="popLayout" initial={false}>
              {filteredOrders.map((order, index) => (
                <motion.li
                  key={order.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2 }}
                  ref={index === lastLoadedIndexRef.current ? lastOrderRef : null}
                  onClick={() => handleRowClick(order)}
                  className="cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-bg"
                >
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <i className="fas fa-receipt text-primary-600 text-xl" />
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center">
                            <h3 className="text-sm font-medium text-primary-600">
                              Order #{order.id.slice(0, 8)}
                            </h3>
                            <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full whitespace-nowrap truncate text-ellipsis overflow-hidden max-w-[8rem]">
                              Table #{order.table.number}
                            </span>
                          </div>
                          <div className="mt-2 sm:flex sm:justify-between">
                            <div className="sm:flex">
                              <p className="flex items-center text-sm text-gray-500 dark:text-dark-text-secondary">
                                <i className="fas fa-clock mr-1.5 text-gray-400" />
                                {formatDate(order.updated_at || order.created_at)}
                              </p>
                              <p className="mt-2 flex items-center text-sm text-gray-500 dark:text-dark-text-secondary sm:mt-0 sm:ml-6">
                                <i className="fas fa-euro-sign mr-1.5 text-gray-400" />
                                {formatCurrency(order.total)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        {/* Status Badge */}
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClasses(order.status)}`}>
                          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        </span>

                        {/* Status Update Buttons */}
                        {order.status !== 'paid' && (
                          <div className="flex space-x-2">
                            {order.status === 'pending' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateOrderStatus(order.id, 'preparing');
                                }}
                                className="btn btn-icon btn-primary"
                                title="Mark as preparing"
                              >
                                <i className="fas fa-fire" />
                              </button>
                            )}
                            {order.status === 'preparing' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateOrderStatus(order.id, 'ready');
                                }}
                                className="btn btn-icon btn-success"
                                title="Mark as ready"
                              >
                                <i className="fas fa-check" />
                              </button>
                            )}
                            {order.status === 'ready' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateOrderStatus(order.id, 'delivered');
                                }}
                                className="btn btn-icon btn-secondary"
                                title="Mark as delivered"
                              >
                                <i className="fas fa-server" />
                              </button>
                            )}
                            {order.status === 'delivered' && (
                              <Link
                                to={`/payment/${order.id}`}
                                className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300"
                                title="Process payment"
                              >
                                <i className="fas fa-euro-sign" />
                              </Link>
                            )}
                          </div>
                        )}

                        {/* View Order Details */}
                        <Link
                          to={`/orders/${order.id}`}
                          className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                        >
                          <i className="fas fa-chevron-right" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>

          {/* Load More Button with animation */}
          {hasMore && (
            <motion.div 
              className="px-4 py-4 sm:px-6 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
            >
              <button
                onClick={handleLoadMore}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                {loading ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center"
                  >
                    <i className="fas fa-spinner fa-spin mr-2" />
                    Loading...
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center"
                  >
                    <i className="fas fa-chevron-down mr-2" />
                    Load More
                  </motion.div>
                )}
              </button>
            </motion.div>
          )}

          {/* Empty State */}
          {filteredOrders.length === 0 && (
            <div className="text-center py-12">
              <i className="fas fa-receipt text-4xl text-gray-400 dark:text-dark-text-secondary mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text-primary mb-2">
                No orders found
              </h3>
              <p className="text-gray-500 dark:text-dark-text-secondary">
                {searchQuery || statusFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Start by creating a new order'}
              </p>
            </div>
          )}
        </div>

        {/* Paid Order Details Modal */}
        {selectedPaidOrder && (
          <PaidOrderDetailsModal
            order={selectedPaidOrder}
            onClose={() => setSelectedPaidOrder(null)}
            onDelete={handleDeletePaidOrder}
          />
        )}
      </div>
    </Profiler>
  );
}